import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPlus, faTrash, faWandMagicSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";
import { TextArea } from "@/components/TextArea/TextArea";
import { useGetAiModelsQuery } from "@/pages/Private/redux/media/media.api";
import { AiModelUseCase, MediaJobType } from "@/pages/Private/redux/media/media.schema";
import {
	MIN_SCENE_DURATION_SECONDS,
	NARRATION_LANGUAGES,
	StoryboardScene,
	TARGET_LENGTHS_SECONDS,
	VideoWizard,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { ModelSelectRow } from "../ModelSelectRow";
import { isLengthExact, isStepApproved, maxSceneDurationOf, stepOf } from "../wizard.utils";

interface Step1StoryboardProps {
	wizard: VideoWizard;
	isBusy: boolean;
	onPatch: (patch: {
		prompt?: string;
		targetLengthSeconds?: number;
		narrationLanguage?: string;
	}) => void;
	onSelectModel: (aiModelId: number) => void;
	onGenerate: (feedback?: string) => void;
	onSelectVersion: (versionId: number) => void;
	onUpdateScene: (
		sceneId: number,
		patch: { title?: string; description?: string; durationSeconds?: number }
	) => void;
	onAddScene: () => void;
	onDeleteScene: (sceneId: number) => void;
	onApprove: () => void;
}

/**
 * Step 1 — prompt, target length, and the storyboard the model produced.
 *
 * The author iterates here until the scenes add up to the target exactly: that equality is
 * what unlocks step 2, and it is checked by the server, not by this component. Editing a
 * scene in place saves and nothing more; adding or removing one resets the steps after it.
 */
export const Step1Storyboard: FunctionComponent<Step1StoryboardProps> = ({
	wizard,
	isBusy,
	onPatch,
	onSelectModel,
	onGenerate,
	onSelectVersion,
	onUpdateScene,
	onAddScene,
	onDeleteScene,
	onApprove,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	// Local drafts so typing is not a round trip; committed on blur.
	const [prompt, setPrompt] = useState(wizard.prompt ?? "");
	const [feedback, setFeedback] = useState("");

	useEffect(() => {
		setPrompt(wizard.prompt ?? "");
	}, [wizard.prompt]);

	/**
	 * A scene's ceiling belongs to step 4's model, not to step 1 — but step 1 is where the
	 * length is typed, so this is where it has to be enforced. Same cache entry the step-4
	 * picker reads, so the list costs nothing here.
	 */
	const { data: sceneVideoModels } = useGetAiModelsQuery({
		useCase: AiModelUseCase.SCENE_VIDEO,
	});
	const maxSceneDuration = maxSceneDurationOf(wizard, sceneVideoModels);

	const { storyboard, targetLengthSeconds } = wizard;
	const narrationLanguage = wizard.narrationLanguage ?? NARRATION_LANGUAGES[0];
	const state = stepOf(wizard, 1);
	const approved = isStepApproved(wizard, 1);
	const lengthOk = isLengthExact(wizard);
	const hasStoryboard = storyboard.scenes.length > 0;
	// A queued or running storyboard job is the only thing that makes this step "working".
	const isGenerating = wizard.activeJobs.some((job) => job.type === MediaJobType.STORYBOARD);

	const commitPrompt = () => {
		if (prompt !== (wizard.prompt ?? "")) {
			onPatch({ prompt });
		}
	};

	return (
		<div className="space-y-4">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h2 className="text-lg font-bold text-ink">{ts("steps.storyboard.heading")}</h2>
					<p className="mt-1 max-w-[640px] text-xs text-subtle">
						{ts("steps.storyboard.description")}
					</p>
				</div>
				<span
					className={classNames(
						"shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
						approved
							? "bg-success-light text-text"
							: hasStoryboard
								? "bg-status-running-light text-warning-ink"
								: "bg-surface text-subtle"
					)}
				>
					{approved
						? ts("steps.storyboard.approvedBadge")
						: hasStoryboard
							? ts("status.awaitingApproval")
							: ts("status.notStarted")}
				</span>
			</header>

			<ModelSelectRow
				disabled={isBusy || isGenerating}
				step={1}
				useCase={AiModelUseCase.STORYBOARD}
				wizard={wizard}
				onSelectModel={onSelectModel}
			/>

			<section className="space-y-3 rounded-card border border-border-soft bg-white p-4">
				<div className="flex flex-wrap gap-4">
					<div className="min-w-[280px] flex-[3]">
						<TextArea
							showError
							handleBlur={commitPrompt}
							handleChange={(event) => setPrompt(event.target.value)}
							label={ts("steps.storyboard.promptLabel")}
							maxLength={4000}
							name="wizard-prompt"
							placeholder={ts("steps.storyboard.promptPlaceholder")}
							value={prompt}
						/>
					</div>
					<div className="min-w-[160px] flex-1">
						<Dropdown
							data={TARGET_LENGTHS_SECONDS.map((seconds) => ({
								id: seconds,
								title: ts("steps.storyboard.lengthOption", { seconds }),
							}))}
							disabled={isBusy || isGenerating}
							// Only on a real change. `Dropdown` re-emits its own value from an effect
							// whose deps include the inline handler, so an unguarded patch here writes
							// on every render — which loops: patch, version bump, refetch, render.
							handleSelect={(item) =>
								Number(item.id) !== targetLengthSeconds &&
								onPatch({ targetLengthSeconds: Number(item.id) })
							}
							label={ts("steps.storyboard.lengthLabel")}
							value={{
								id: targetLengthSeconds,
								title: ts("steps.storyboard.lengthOption", { seconds: targetLengthSeconds }),
							}}
						/>
						<p className="mt-1 text-xs text-subtle">{ts("steps.storyboard.lengthHint")}</p>

						{/*
						 * Under the length rather than beside the voice in step 3, because the
						 * storyboard is written *in* it: by the time a voice is being chosen the
						 * scenes already exist, and changing it then throws them away.
						 */}
						<div className="mt-3">
							<Dropdown
								data={NARRATION_LANGUAGES.map((code) => ({
									id: code,
									title: ts(`languages.${code}`),
								}))}
								disabled={isBusy || isGenerating}
								// Guarded like the length above, and for the same reason.
								handleSelect={(item) =>
									String(item.id) !== narrationLanguage &&
									onPatch({ narrationLanguage: String(item.id) })
								}
								label={ts("steps.storyboard.languageLabel")}
								value={{ id: narrationLanguage, title: ts(`languages.${narrationLanguage}`) }}
							/>
							<p className="mt-1 text-xs text-subtle">{ts("steps.storyboard.languageHint")}</p>
						</div>
					</div>
				</div>

				{!hasStoryboard && !isGenerating && (
					<>
						<Button
							className="w-full"
							color={ButtonColor.ACTION}
							disabled={isBusy || !prompt.trim()}
							image={<Icon className="mr-2" icon={faWandMagicSparkles} />}
							size={ButtonSize.M}
							title={ts("steps.storyboard.generate")}
							type="button"
							onClick={() => onGenerate()}
						/>
						{/* The guard stated, not just enforced: an empty prompt buys an invented
						    storyboard, so the button will not fire without one. */}
						{!prompt.trim() && (
							<p className="text-xs text-subtle">{ts("steps.storyboard.promptRequired")}</p>
						)}
					</>
				)}
			</section>

			{isGenerating && (
				<div className="flex items-center gap-3 rounded-card border border-border-soft bg-white px-4 py-6">
					<Spinner />
					<span className="text-sm font-semibold text-subtle">
						{ts("steps.storyboard.generating")}
					</span>
				</div>
			)}

			{!hasStoryboard && !isGenerating && (
				<div className="rounded-card border border-dashed border-border bg-surface px-4 py-8 text-center text-sm text-subtle">
					{ts("steps.storyboard.empty")}
				</div>
			)}

			{hasStoryboard && (
				<>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<span className="text-sm font-bold text-ink">
							{ts("steps.storyboard.versionHeading", {
								version:
									storyboard.versions.find((version) => version.isActive)?.versionNumber ?? 1,
								note:
									storyboard.versions.find((version) => version.isActive)?.note ??
									ts("steps.storyboard.noNote"),
							})}
						</span>
						<div className="flex flex-wrap gap-1.5">
							{storyboard.versions.map((version) => (
								<button
									key={version.id}
									className={classNames(
										"rounded-full border px-2.5 py-1 text-xs font-bold transition-colors",
										version.isActive
											? "border-accent bg-accent-tint text-text"
											: "border-border bg-white text-subtle hover:border-accent"
									)}
									disabled={isBusy || isGenerating || version.isActive}
									type="button"
									onClick={() => onSelectVersion(version.id)}
								>
									v{version.versionNumber}
								</button>
							))}
						</div>
					</div>

					{storyboard.scenes.map((scene, index) => (
						<SceneRow
							key={scene.id}
							// Frozen during a regeneration: the new version replaces the whole
							// storyboard, and an edit typed meanwhile would be silently lost.
							disabled={isBusy || isGenerating}
							index={index}
							maxDurationSeconds={maxSceneDuration}
							scene={scene}
							onDelete={() => onDeleteScene(scene.id)}
							onUpdate={(patch) => onUpdateScene(scene.id, patch)}
						/>
					))}

					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={isBusy || isGenerating}
						image={<Icon className="mr-2" icon={faPlus} />}
						size={ButtonSize.S}
						title={ts("steps.storyboard.addScene")}
						type="button"
						onClick={onAddScene}
					/>

					{/* The gate itself, stated plainly: the total has to hit the target exactly. */}
					<div
						className={classNames(
							"flex flex-wrap items-center justify-between gap-2 rounded-card px-4 py-3 text-sm font-bold text-text",
							lengthOk ? "bg-success-light" : "bg-error-light"
						)}
					>
						<span>
							{ts("steps.storyboard.totalLength", {
								seconds: storyboard.totalDurationSeconds,
							})}
						</span>
						<span>
							{lengthOk
								? ts("steps.storyboard.targetMet", { seconds: targetLengthSeconds })
								: ts("steps.storyboard.targetMissed", { seconds: targetLengthSeconds })}
						</span>
					</div>

					<section className="space-y-3 rounded-card border border-border-soft bg-surface p-4">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<span className="text-sm font-bold text-ink">
								{ts("steps.storyboard.iterateHeading")}
							</span>
							<span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-subtle">
								{ts("steps.storyboard.iterations", {
									count: state?.generationCount ?? 0,
								})}
							</span>
						</div>

						<TextArea
							showError
							handleChange={(event) => setFeedback(event.target.value)}
							name="wizard-feedback"
							placeholder={ts("steps.storyboard.feedbackPlaceholder")}
							value={feedback}
						/>

						<div className="flex flex-wrap gap-2">
							<Button
								className="flex-1"
								color={ButtonColor.ACTION_SECONDARY}
								disabled={isBusy || isGenerating || !feedback.trim()}
								size={ButtonSize.M}
								title={ts("steps.storyboard.regenerate")}
								type="button"
								onClick={() => {
									onGenerate(feedback.trim());
									setFeedback("");
								}}
							/>
							<Button
								color={ButtonColor.ACTION}
								disabled={isBusy || !lengthOk}
								size={ButtonSize.M}
								title={ts("steps.storyboard.approve")}
								// The disabled button says nothing on its own; this explains the gate.
								type="button"
								onClick={onApprove}
							/>
						</div>
						{!lengthOk && (
							<p className="text-xs text-subtle">{ts("steps.storyboard.approveBlockedHint")}</p>
						)}
					</section>
				</>
			)}
		</div>
	);
};

interface SceneRowProps {
	scene: StoryboardScene;
	index: number;
	/** What step 4's model will actually render — see `maxSceneDurationOf`. */
	maxDurationSeconds: number;
	disabled?: boolean;
	onUpdate: (patch: { title?: string; description?: string; durationSeconds?: number }) => void;
	onDelete: () => void;
}

/**
 * One scene, editable in place. Edits commit on blur rather than on every keystroke — each
 * one is a round trip that returns the whole aggregate, and a per-character save would
 * fight the author's cursor.
 */
const SceneRow: FunctionComponent<SceneRowProps> = ({
	scene,
	index,
	maxDurationSeconds,
	disabled,
	onUpdate,
	onDelete,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const [title, setTitle] = useState(scene.title);
	const [description, setDescription] = useState(scene.description ?? "");
	const [duration, setDuration] = useState(String(scene.durationSeconds));

	useEffect(() => {
		setTitle(scene.title);
		setDescription(scene.description ?? "");
		setDuration(String(scene.durationSeconds));
	}, [scene.title, scene.description, scene.durationSeconds]);

	// The API rejects anything outside the bounds, so the field never sends one.
	const commitDuration = () => {
		const parsed = Number(duration.replace(",", "."));
		const clamped = Math.min(
			maxDurationSeconds,
			Math.max(MIN_SCENE_DURATION_SECONDS, Number.isFinite(parsed) ? parsed : scene.durationSeconds)
		);

		setDuration(String(clamped));

		if (clamped !== scene.durationSeconds) {
			onUpdate({ durationSeconds: clamped });
		}
	};

	return (
		<div className="flex gap-3 rounded-card border border-border-soft bg-white p-3">
			<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface text-xs font-bold text-subtle">
				{index + 1}
			</span>
			<div className="min-w-0 flex-1 space-y-2">
				<div className="flex flex-wrap items-center gap-2">
					<input
						aria-label={ts("steps.storyboard.sceneTitle")}
						className="min-w-[200px] flex-1 rounded-input border border-border bg-white px-2.5 py-1.5 text-sm font-bold text-ink outline-none focus:border-accent"
						disabled={disabled}
						value={title}
						onBlur={() => title !== scene.title && onUpdate({ title })}
						onChange={(event) => setTitle(event.target.value)}
					/>
					<span className="flex items-center gap-1.5">
						<input
							aria-label={ts("steps.storyboard.sceneDuration")}
							className="w-16 rounded-input border border-border bg-white px-2.5 py-1.5 text-right text-sm font-bold text-ink outline-none focus:border-accent"
							disabled={disabled}
							inputMode="decimal"
							title={ts("steps.storyboard.durationBounds", {
								min: MIN_SCENE_DURATION_SECONDS,
								max: maxDurationSeconds,
							})}
							value={duration}
							onBlur={commitDuration}
							onChange={(event) => setDuration(event.target.value)}
						/>
						<span className="text-xs font-bold text-subtle">{ts("steps.storyboard.seconds")}</span>
						{/* The bounds the blur will clamp to — no more numbers nobody typed. */}
						<span className="text-xs text-subtle">
							{ts("steps.storyboard.durationBounds", {
								min: MIN_SCENE_DURATION_SECONDS,
								max: maxDurationSeconds,
							})}
						</span>
					</span>
				</div>

				<textarea
					aria-label={ts("steps.storyboard.sceneDescription")}
					className="w-full rounded-input border border-border bg-white px-2.5 py-1.5 text-xs text-text outline-none focus:border-accent"
					disabled={disabled}
					rows={2}
					value={description}
					onBlur={() => description !== (scene.description ?? "") && onUpdate({ description })}
					onChange={(event) => setDescription(event.target.value)}
				/>

				<div className="flex flex-wrap items-center gap-2">
					{scene.tags.map((tag) => (
						<span
							key={tag}
							className="rounded-full bg-surface px-2 py-0.5 text-xs font-semibold text-subtle"
						>
							{tag}
						</span>
					))}
					<button
						className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-danger disabled:opacity-60"
						disabled={disabled}
						type="button"
						onClick={onDelete}
					>
						<Icon icon={faTrash} />
						{ts("steps.storyboard.removeScene")}
					</button>
				</div>
			</div>
		</div>
	);
};
